import React from 'react';

const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
};

const Row = ({ label, value }) => value != null && value !== '' ? (
    <tr>
        <td className="app-confirm__label">{label}</td>
        <td className="fw-bold">{value}</td>
    </tr>
) : null;

const PersonSummary = ({ title, data }) => {
    const salutationLabel = { ms: 'Frau', mr: 'Herr', neutral: 'Neutrale Anrede' }[data.salutation] || '';
    return (
        <section className="app-confirm__section">
            <h3>{title}</h3>
            <table className="app-confirm__table mb-3">
                <tbody>
                    <Row label="Anrede" value={salutationLabel} />
                    <Row label="Vorname" value={data.forename} />
                    <Row label="Nachname" value={data.lastname} />
                    <Row label="Adresse" value={data.address} />
                    <Row label="PLZ" value={data.zip} />
                    <Row label="Ort" value={data.city} />
                    <Row label="Telefon" value={data.phone} />
                    <Row label="E-Mail" value={data.email} />
                    <Row label="Geburtsdatum" value={formatDate(data.dateOfBirth)} />
                    <Row label="Beruf" value={data.occupation} />
                    <Row label="Monatliches Bruttoeinkommen" value={data.monthlyIncome ? `CHF ${data.monthlyIncome}` : ''} />
                    <Row label="GWG-Mieter:in" value={data.gwgTenant === 'yes' ? 'Ja' : data.gwgTenant === 'no' ? 'Nein' : ''} />
                    {data.gwgTenant === 'no' && (
                        <Row label="Betreibungsauskunft" value={data.debtInformation ? data.debtInformation.name : '(keine Datei)'} />
                    )}
                </tbody>
            </table>

            {(data.employerCompany || data.employer?.company || data.employerPerson || data.employer?.person || data.employerPhone || data.employer?.phone) && (
                <>
                    <h4>Arbeitgeber:in</h4>
                    <table className="app-confirm__table mb-3">
                        <tbody>
                            <Row label="Firma" value={data.employerCompany || data.employer?.company} />
                            <Row label="Referenzperson" value={data.employerPerson || data.employer?.person} />
                            <Row label="Telefon" value={data.employerPhone || data.employer?.phone} />
                            <Row label="Beruf" value={data.employerOccupation || data.employer?.occupation} />
                        </tbody>
                    </table>
                </>
            )}

            {(data.landlordName || data.landlord?.name || data.landlordPhone || data.landlord?.phone) && (
                <>
                    <h4>Vermieter:in</h4>
                    <table className="app-confirm__table mb-3">
                        <tbody>
                            <Row label="Firma/Name" value={data.landlordName || data.landlord?.name} />
                            <Row label="Telefon" value={data.landlordPhone || data.landlord?.phone} />
                        </tbody>
                    </table>
                </>
            )}
        </section>
    );
};

const AppConfirm = ({ values, registration, submitting, submitError, prevStep, onSubmit, focusedTitleRef }) => {
    const apt = registration?.apartment;
    const parkingLabel = { garage: 'Garage', outdoors: 'Aussenparkplatz', '': 'Kein Parkplatz' }[values.parking] ?? 'Kein Parkplatz';
    const availabilityDisplay = apt?.availableByAgreement ? 'Nach Vereinbarung' : formatDate(apt?.availabilityDate);

    return (
        <div className="app-confirm">
            <h2 ref={focusedTitleRef} tabIndex={-1}>Zusammenfassung</h2>

            <section className="app-confirm__section">
                <h2 className="mt-4">Wohnung</h2>
                {apt && (
                    <>
                        <p className="fw-bold">{apt.title}</p>
                        <table className="app-confirm__table mb-3">
                            <tbody>
                                {apt.address && <Row label="Adresse" value={`${apt.address}${apt.zip ? `, ${apt.zip}` : ''}${apt.city ? ` ${apt.city}` : ''}`} />}
                                {apt.numberOfRooms && <Row label="Zimmer" value={apt.numberOfRooms} />}
                                {apt.surfaceArea && <Row label="Fläche" value={`${apt.surfaceArea} m²`} />}
                                {apt.floor && <Row label="Etage" value={apt.floor} />}
                                {apt.rentPerMonth && <Row label="Miete" value={`CHF ${Number(apt.rentPerMonth).toLocaleString('de-CH')} / Monat`} />}
                                {availabilityDisplay && <Row label="Verfügbar ab" value={availabilityDisplay} />}
                                <Row label="Parkplatz" value={parkingLabel} />
                                <Row label="Gewünschter Bezugstermin" value={formatDate(values.moveinDate)} />
                                <Row label="Bemerkungen" value={values.apartmentComments} />
                            </tbody>
                        </table>
                    </>
                )}
            </section>

            <section className="app-confirm__section">
                <h2>Haushalt</h2>
                <table className="app-confirm__table mb-4">
                    <tbody>
                        <Row label="Grund des Wohnungswechsels" value={values.motivation} />
                        <Row label="Anzahl Erwachsene" value={values.numberAdults} />
                        <Row label="Kinder" value={values.children === 'yes' ? 'Ja' : values.children === 'no' ? 'Nein' : ''} />
                        {values.children === 'yes' && (
                            <>
                                <Row label="Anzahl Kinder" value={values.numberChildren} />
                                <Row label="Jahrgänge" value={values.birthyearsChildren} />
                            </>
                        )}
                        <Row label="Haustiere" value={values.pets === 'yes' ? 'Ja' : values.pets === 'no' ? 'Nein' : ''} />
                        {values.pets === 'yes' && <Row label="Art/Rasse" value={values.petsType} />}
                    </tbody>
                </table>
            </section>

            <h2>Personalien</h2>
            <PersonSummary title="Mietinteressent:in" data={values} />

            {values.coApplicants.map((ca, i) => (
                <PersonSummary key={i} title={`Person ${i + 2}`} data={ca} />
            ))}

            {submitError && (
                <div id="af_errors" className="errors mb-4">
                    <div className="alert-danger">
                        <p>{submitError}</p>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-between flex-row-reverse mt-5">
                <button className="btn btn-secondary" onClick={onSubmit} disabled={submitting}>
                    {submitting ? 'Wird eingereicht…' : 'Jetzt bewerben'}
                </button>
                <button className="btn btn-secondary btn-back" onClick={prevStep} disabled={submitting}>Zurück</button>
            </div>
        </div>
    );
};

export default AppConfirm;
